import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useRef } from 'react';
import { GET_USERS, GET_BOATS, GET_USER_BOAT, GET_ALL_OBSERVATIONS, GET_DATA_OBSERVATIONS } from "@/components/elements/queries";
import {Query, makeQuery } from "./AdminContextServices"
import { COLORS } from "../components/ui/colors";
import { useSubscription } from "@apollo/client";

import type { User, Boat, UserBateau, ObservationType } from '@/components/types/types';


interface AdminProviderProps {
    children: ReactNode;
}

interface AdminContextType {
    loading: boolean;
    initialLoadDone: boolean;
    allObservations: ObservationType[];
    usersQuery: Query<{users: User[]}>,
    boatsQuery: Query<{bateaux: Boat[]}>,
    usersBoats: Query<{users_bateaux: UserBateau[]}>,
    tabIdUser2Boat: {[key: string]: string[]} | null;
    tabIdBoat2User: {[key: string]: string[]} | null;
    tabIdUsers: { [key: string]: string };
    tabIdBoats: { [key: string]: {name: string, code: string} };
    colorMapCache : Record<string, string>;
}

const generateColorMap = (boatIds: string[]) => {
    let colors: { [key: string]: string } = {};
    boatIds.forEach((boatId) => {
        const index = Math.abs(boatId.split("").reduce((acc: any, char: any) => acc + char.charCodeAt(0), 0)) % COLORS.length;
        colors[boatId] = COLORS[index];
    });

    //const index = Math.abs("Inconnu".split("").reduce((acc: any, char: any) => acc + char.charCodeAt(0), 0)) % COLORS.length;
    //colors["Inconnu"] = COLORS[index];

    return colors;

  };

const AdminContext = createContext<AdminContextType | null>(null);

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {

    // Hooks pour les mappings IDs users-bateaux
    const [tabIdUser2Boat, setTabIdUser2Boat] = useState<{ [key: string]: string[] }>({});
    const [tabIdBoat2User, setTabIdBoat2User] = useState<{ [key: string]: string[] }>({});

    const [tabIdUsers, setTabIdUsers] = useState<{ [key: string]: string }>({});
    const [tabIdBoats, setTabIdBoats] = useState<{ [key: string]: {name: string, code: string}}>({});

    const colorMapCache =  useMemo(() => generateColorMap(Object.values(tabIdBoats).map(itm => itm.code)), [tabIdBoats]);
    
    const usersQuery = makeQuery<{users: User[]}>(GET_USERS, "users");
    const boatsQuery = makeQuery<{bateaux: Boat[]}>(GET_BOATS, "boats");
    const usersBoats = makeQuery<{users_bateaux: UserBateau[]}>(GET_USER_BOAT, "usersBoats");

    const appOpenTime = useRef(new Date().toISOString()); // Heure à laquelle on ouvre l'appli, pour la subscription
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const [allObservations, setAllObservations] = useState<ObservationType[]>([]);

    const obsAllQuery = makeQuery<{observations: ObservationType[]}>(GET_ALL_OBSERVATIONS, "obs all");

    // Chargement de l'historique des observations
    useEffect(() => {
        if (obsAllQuery.data && !initialLoadDone) {
            console.log(`Chargé ${obsAllQuery.data.observations.length} observations historiques`);
            setAllObservations(obsAllQuery.data.observations);
            setInitialLoadDone(true);
        }
    }, [obsAllQuery.data, obsAllQuery.loading, initialLoadDone]);

    // Déclaration de la subscription
    const { data, loading, error } = useSubscription<{observations: ObservationType[];}>(GET_DATA_OBSERVATIONS,
        {variables: { since: appOpenTime.current },
        skip: !initialLoadDone,
        }
    );

    // Si loading change
    useEffect(() => {
        console.log("error subscription:", error)
    }, [error])

    // A chaque nouvelle subscription 
    useEffect(() => {
        if (!data?.observations) return;
    
        setAllObservations((prev: ObservationType[]) => {
            const updated = new Map(prev.map((o: ObservationType) => [o.id, o]));
            
            data.observations.forEach(obs => {
            updated.set(obs.id, obs);
        });
        
        return Array.from(updated.values())
        .sort((a, b) => new Date(a.date_creation).getTime() - new Date(b.date_creation).getTime());
    });},[data]);


    // Gestion des tableaux de mapping IDs users-bateaux
   
    useEffect(() => {
        if (usersQuery.loading || usersQuery.error) return;
    
        if(usersQuery.data?.users?.length){
          const tab: { [key: string]: string } = usersQuery.data.users.reduce<{ [key: string]: string }>((acc, user) => {
            acc[user.id] = user.email;
            return acc;
          }, {});
          setTabIdUsers(tab);
        }
    }, [usersQuery.data, usersQuery.loading, usersQuery.error]);

    useEffect(() => {
    
        if (boatsQuery.loading || boatsQuery.error) return;
    
        if(boatsQuery.data?.bateaux?.length){

          const tab: { [key: string]: {name: string, code: string} } = boatsQuery.data.bateaux.reduce<{ [key: string]: {name: string, code: string} }>((acc, boat) => {
            acc[boat.id] = {"name": boat.nom_bateau, "code": boat.code_pf};
            return acc;
          }, {});
          setTabIdBoats(tab);
        }
      }, [boatsQuery.data, boatsQuery.loading, boatsQuery.error]);

     
    useEffect(() => {
        if (usersBoats.loading || usersBoats.error) return;
        if(usersBoats.data?.users_bateaux?.length){

            const tabU2B: { [key: string]: string[] } = usersBoats.data.users_bateaux.reduce<{ [key: string]: string[] }>((acc, ub) => {
                if (!acc[ub.user_id]) {
                    acc[ub.user_id] = [ub.bateau_id];
                }else{
                    acc[ub.user_id].push(ub.bateau_id);
                }
                return acc;
            }, {});

            
            Object.keys(tabIdUsers).forEach(key => {
                tabU2B[key] = tabU2B[key] || [];
            });
           
        
            const tabB2U: { [key: string]: string[] } = usersBoats.data.users_bateaux.reduce<{ [key: string]: string[] }>((acc, ub) => {
                if (!acc[ub.bateau_id]) {
                    acc[ub.bateau_id] = [ub.user_id];
                }else{
                    acc[ub.bateau_id].push(ub.user_id);
                }
                return acc;
            }, {});
            
            Object.keys(tabIdBoats).forEach(key => {
                tabB2U[key] = tabB2U[key] || [];
            });


            setTabIdUser2Boat(tabU2B);
            setTabIdBoat2User(tabB2U);
        }
    }, [usersBoats.data, usersBoats.loading, usersBoats.error, tabIdUsers, tabIdBoats]);


    const contextValues = { 
        loading,
        initialLoadDone,
        allObservations,
        usersQuery,
        boatsQuery,
        usersBoats,
        tabIdUser2Boat,
        tabIdBoat2User,
        tabIdUsers, 
        tabIdBoats,
        colorMapCache 
    };


    return (
      <AdminContext.Provider value={contextValues}>
        {children}
      </AdminContext.Provider>
    );
};


export const useAdminContext = (): AdminContextType => {
    const context = useContext(AdminContext);
    if (!context) {
      throw new Error('useAdminContext doit être utilisé dans un AdminProvider');
    }
    return context;
};
